import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'

/**
 * The user's selected display currency — the currency all monetary values are converted to for
 * presentation. Persisted to localStorage and defaulting to AUD (the canonical currency), mirroring
 * ThemeContext. Conversion itself lives in lib/money + useMoneyFormatter; this only holds the choice.
 */
interface DisplayCurrencyContextValue {
  displayCurrency: string
  setDisplayCurrency: (c: string) => void
}

const STORAGE_KEY = 'aerris.displayCurrency'
const DEFAULT_CURRENCY = 'AUD'
const DisplayCurrencyContext = createContext<DisplayCurrencyContextValue | null>(null)

function readInitial(): string {
  if (typeof window === 'undefined') return DEFAULT_CURRENCY
  const stored = window.localStorage.getItem(STORAGE_KEY)
  return stored && /^[A-Za-z]{3}$/.test(stored) ? stored.toUpperCase() : DEFAULT_CURRENCY
}

export function DisplayCurrencyProvider({ children }: { children: ReactNode }) {
  const [displayCurrency, setState] = useState<string>(readInitial)

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, displayCurrency)
    } catch {
      // ignore quota / privacy-mode errors
    }
  }, [displayCurrency])

  const setDisplayCurrency = useCallback((c: string) => setState(c.toUpperCase()), [])

  return (
    <DisplayCurrencyContext.Provider value={{ displayCurrency, setDisplayCurrency }}>
      {children}
    </DisplayCurrencyContext.Provider>
  )
}

export function useDisplayCurrency(): DisplayCurrencyContextValue {
  const ctx = useContext(DisplayCurrencyContext)
  if (!ctx) throw new Error('useDisplayCurrency must be used within DisplayCurrencyProvider')
  return ctx
}
